import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { API } from 'aws-amplify';
import axios from 'axios';
import { Menu, MenuItem, ListItemIcon, ListItemText, Select, FormControl } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  ChatWrapper,
  TextArea,
  ActionsContainer,
  LeftActions,
  RightActions,
  IconButton,
  SendIconButton,
  FileAttachmentContainer,
  StopButton,
  IsUploadingWrapper,
  ErrorMessageContainer,
  StyledTooltip,
  WebSearchButton,
  ReasoningAgentButton
} from './ChatInput.styled';
import { Icon } from '../..';
import { getUploadUrlFile } from '../../../graphql/queries';
import { UploadToVaultModal } from '../../../components/DocumentCanvas/modals';

const ChatInput = ({
  newMessage,
  onChange,
  handleSendMessage,
  onKeyPress,
  placeholder = 'Ask anything...',
  showSpinner = false,
  disableSend = false,
  showFileUpload = true,
  showClearChat = true,
  showWebSearch = false,
  webSearchEnabled = false,
  onWebSearchToggle,
  onFileUploaded,
  onCleanThread,
  customerId,
  stop = false,
  onStopClick,
  maxHeight = '150px', 
  textAreaRef,
  showReasoningAgent = false,
  reasoningModeEnabled = false,
  onReasoningAgentClick,
  showDocuments = false,
  documentCount = 0,
  onDocumentsClick,
  showModelSelector = false,
  selectedModel,
  onModelChange,
  onUploadToVault,
  currentAgent,
  availableAgents,
}) => {
  const [fileUploaded, setFileUploaded] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const filetextAreaRef = useRef(null);
  const inputRef = useRef(null);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    if (textAreaRef) {
      textAreaRef.current = inputRef.current;
    }
  }, [textAreaRef]);

  useEffect(() => {
    if (inputRef.current) {
      const textarea = inputRef.current;
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(parseInt(maxHeight, 10), Math.max(60, scrollHeight))}px`;
    }
  }, [newMessage, maxHeight]);

  const clearFileAttachment = () => {
    setFileUploaded('');
    if (onFileUploaded) {
      onFileUploaded('');
    }
    if (filetextAreaRef.current) {
      filetextAreaRef.current.value = '';
    }
  };

  // Clear input text
  const clearInput = () => {
    if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  const sendMessage = () => {
    if (!newMessage || newMessage.trim() === '' || showSpinner || isUploading) {
      return;
    }

    // Pass web search preference to parent
    handleSendMessage(newMessage, { useWebSearch: webSearchEnabled }); 

    clearInput();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      return false;
    }
    
    if (onKeyPress && e.key !== 'Enter') {
      onKeyPress(e, clearInput);
    }
  };

  const handleCleanThread = () => {
    if (onCleanThread) {
      onCleanThread();
      clearInput();
      clearFileAttachment();
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleWebSearchToggle = () => {
    if (onWebSearchToggle) {
      onWebSearchToggle(!webSearchEnabled);
    }
    handleMenuClose();
  };

  const handleReasoningAgentToggle = () => {
    if (onReasoningAgentClick) {
      onReasoningAgentClick();
    }
    handleMenuClose();
  };

  const handleFile = async (files) => {
    if (!files.length) return;

    const file = files[0];
    const allowedExtensions = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/pdf',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedExtensions.includes(file.type)) {
      setErrorMessage('Please upload a valid file: *.doc, *.docx, *.pdf, *.txt, *.csv, *.xlsx.');
      return;
    }

    if (file.size / 1e6 > 100) {
      setErrorMessage('The file exceeds the size limit of 100 MB.');
      return;
    }

    // Store the file and open the upload modal
    setPendingFile(file);
    setShowUploadModal(true);
  };

  const handleUploadModalClose = () => {
    setShowUploadModal(false);
    setPendingFile(null);
    // Clear the file input
    if (filetextAreaRef.current) {
      filetextAreaRef.current.value = '';
    }
  };

  const handleUploadComplete = async (file, selectedDestinations, selectedAgents) => {
    if (!onUploadToVault) {
      setErrorMessage('Upload to vault not supported in this context.');
      setShowUploadModal(false);
      return;
    }

    try {
      setIsUploading(true);
      setShowUploadModal(false);
      
      // Call the upload handler from HomePage with both destinations and agents
      // Wait for API confirmation that document is assigned/added
      const s3Url = await onUploadToVault(file, selectedDestinations, selectedAgents);
      
      if (s3Url) {
        // Format file size
        const formatFileSize = (bytes) => {
          if (!bytes) return '0 B';
          const k = 1024;
          const sizes = ['B', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        };
        
        const fileExt = file.name.split('.').pop().toLowerCase();
        
        let fileType = 'file';
        if (['pdf'].includes(fileExt)) fileType = 'PDF Document';
        else if (['doc', 'docx'].includes(fileExt)) fileType = 'Word Document';
        else if (['xls', 'xlsx', 'csv'].includes(fileExt)) fileType = 'Spreadsheet';
        else if (['txt'].includes(fileExt)) fileType = 'Text Document';
        
        // Set attachment data
        if (onFileUploaded) {
          onFileUploaded({ 
            url: s3Url, 
            fileName: file.name,
            fileSize: formatFileSize(file.size)
          });
        }
        
        // Show file message in chat ONLY if agents were selected
        if (selectedAgents && selectedAgents.length > 0 && handleSendMessage) {
          console.log('📄 Showing file message in chat (agents selected)');
          // Display file message locally (no API call) - pass file data directly
          handleSendMessage('', { 
            isFileUpload: true,
            fileData: {
              name: file.name,
              type: fileType,
              size: formatFileSize(file.size)
            }
          });
        } else {
          console.log('📁 No file message in chat (only drives/folders selected)');
        }
      }
    } catch (err) {
      console.error('Error uploading to vault:', err);
      setErrorMessage('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      setPendingFile(null);
    }
  };

  
  const removeSpecialCharacters = (string) => {
    return string.replace(/\s/g, '_').replace(/[^.\w\s]/gi, '');
  };

  const getFileTypeLabel = (mimeType) => {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return 'spreadsheet';
    if (mimeType.includes('text/plain')) return 'text';
    return 'uploaded';
  };

  const handleFileChange = (e) => {
    if (e.target.files?.length > 0) {
      handleFile(e.target.files);
    }
  };

  return (
    <>
      <UploadToVaultModal
        isOpen={showUploadModal}
        onClose={handleUploadModalClose}
        onUpload={handleUploadComplete}
        file={pendingFile}
        currentAgent={currentAgent}
        availableAgents={availableAgents || []}
      />
      
      <ChatWrapper>
        {errorMessage && (
        <ErrorMessageContainer>
          <IconButton onClick={() => setErrorMessage('')}>
            <Icon name="close" height="12px" width="12px" />
          </IconButton>
          {errorMessage}
        </ErrorMessageContainer>
      )}

      <TextArea
        ref={inputRef}
        value={newMessage || ''}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        placeholder={reasoningModeEnabled ? "🧠 Ask the AI Reasoning Agent..." : (isUploading ? "Uploading document..." : placeholder)}
        rows={1}
        disabled={isUploading}
      />


      <ActionsContainer>
        <LeftActions>
          {/* Documents button first (left-most) */}
          {showDocuments && (
            <IconButton onClick={onDocumentsClick}>
              <StyledTooltip title="Your documents" arrow placement="top">
                <div style={{ position: 'relative' }}>
                  <DescriptionIcon style={{ fontSize: '18px' }} />
                  {documentCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      background: '#4F46E5',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: '700',
                      padding: '2px 4px',
                      borderRadius: '8px',
                      minWidth: '14px',
                      textAlign: 'center'
                    }}>
                      {documentCount}
                    </span>
                  )}
                </div>
              </StyledTooltip>
            </IconButton>
          )}

          {/* Three-dot menu second */}
          {(showWebSearch || showReasoningAgent) && (
            <>
              <IconButton 
                onClick={handleMenuOpen}
                aria-controls={menuOpen ? 'chat-options-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                <StyledTooltip title="More options" arrow placement="top">
                  <span>
                    <MoreVertIcon style={{ fontSize: '18px' }} />
                  </span>
                </StyledTooltip>
              </IconButton>

              <Menu
                id="chat-options-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                {showReasoningAgent && (
                  <MenuItem onClick={handleReasoningAgentToggle}>
                    <ListItemIcon>
                      <Icon name="annBrainIcon" height="18px" width="18px" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={reasoningModeEnabled ? "Reasoning mode: ON" : "Reasoning mode: OFF"}
                      secondary={reasoningModeEnabled ? "Next message goes to AI" : "Click to enable"}
                      primaryTypographyProps={{
                        style: { 
                          fontSize: '14px',
                          fontWeight: reasoningModeEnabled ? 600 : 400,
                          color: reasoningModeEnabled ? '#4F46E5' : 'inherit'
                        }
                      }}
                      secondaryTypographyProps={{
                        style: { fontSize: '12px' }
                      }}
                    />
                  </MenuItem>
                )}
                
                {showWebSearch && (
                  <MenuItem onClick={handleWebSearchToggle}>
                    <ListItemIcon>
                      <Icon name="webSearch" height="18px" width="18px" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={webSearchEnabled ? "Web search: ON" : "Web search: OFF"}
                      secondary={webSearchEnabled ? "Searching the web" : "Click to enable"}
                      primaryTypographyProps={{
                        style: { 
                          fontSize: '14px',
                          fontWeight: webSearchEnabled ? 600 : 400,
                          color: webSearchEnabled ? '#1976d2' : 'inherit'
                        }
                      }}
                      secondaryTypographyProps={{
                        style: { fontSize: '12px' }
                      }}
                    />
                  </MenuItem>
          )}
              </Menu>
            </>
          )}

          {/* Attach document button second */}
          {showFileUpload && (
            <>
              <input
                type="file"
                id="file-upload"
                ref={filetextAreaRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".doc,.docx,.pdf,.txt,.csv,.xlsx"
              />
              <IconButton 
                as="label" 
                htmlFor="file-upload" 
                showSpinner={isUploading}
              >
                {isUploading ? (
                  <IsUploadingWrapper/>
                ) : (
                  <StyledTooltip title="Upload document" arrow placement="top">
                    <span>
                      <Icon name="attachmentIcon" height="18px" width="9px" />
                    </span>
                  </StyledTooltip>
                )}
              </IconButton>
            </>
          )}

          {/* Clean chat button third */}
          {showClearChat && (
            <IconButton onClick={handleCleanThread}>
              <StyledTooltip title="Create new chat" arrow placement="top">
                <span>
                  <Icon name="newChat2Icon" height="18px" width="18px" />
                </span>
              </StyledTooltip>
            </IconButton>
          )}

          {/* File uploaded indicator removed - file message appears in chat instead */}
          
          {/* Model Selector - Integrated in LeftActions */}
          {showModelSelector && selectedModel && onModelChange && (
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginLeft: '8px',
              paddingLeft: '8px',
              borderLeft: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <span style={{ fontSize: '11px', color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>Model:</span>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedModel}
                  onChange={(e) => onModelChange(e.target.value)}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    height: '26px',
                    '& .MuiSelect-select': {
                      padding: '3px 8px',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.2)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4F46E5',
                      borderWidth: '1px',
                    },
                  }}
                >
                  <MenuItem value="gpt-5.2" sx={{ fontSize: '11px' }}>
                    🤖 GPT-5.2
                  </MenuItem>
                  <MenuItem value="gpt-5.2-pro-2025-12-11" sx={{ fontSize: '11px' }}>
                    ⚡ GPT-5.2 Pro
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          )}
        </LeftActions>

        <RightActions>
          {stop ? (
            <StopButton onClick={onStopClick} title="Stop">
              <span></span>
            </StopButton>
          ) : (
            <SendIconButton 
              onClick={sendMessage}
              showSpinner={!newMessage || !newMessage.trim() || showSpinner || isUploading}
              title={isUploading ? "Uploading document..." : "Send message"}
              disabled={isUploading}
            >
              <Icon name="arrowWhiteUp2" height="8px" width="8px" />
            </SendIconButton>
          )}
        </RightActions>
      </ActionsContainer>
      </ChatWrapper>
    </>
  );
};

ChatInput.propTypes = {
  newMessage: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  handleSendMessage: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string,
  showSpinner: PropTypes.bool,
  disableSend: PropTypes.bool,
  showFileUpload: PropTypes.bool,
  showClearChat: PropTypes.bool,
  showWebSearch: PropTypes.bool,
  webSearchEnabled: PropTypes.bool,
  onWebSearchToggle: PropTypes.func,
  onFileUploaded: PropTypes.func,
  onCleanThread: PropTypes.func,
  customerId: PropTypes.string,
  stop: PropTypes.bool,
  onStopClick: PropTypes.func,
  maxHeight: PropTypes.string,
  textAreaRef: PropTypes.shape({ current: PropTypes.any }),
  showReasoningAgent: PropTypes.bool,
  reasoningModeEnabled: PropTypes.bool,
  onReasoningAgentClick: PropTypes.func,
  showDocuments: PropTypes.bool,
  documentCount: PropTypes.number,
  onDocumentsClick: PropTypes.func,
  showModelSelector: PropTypes.bool,
  selectedModel: PropTypes.string,
  onModelChange: PropTypes.func,
  onUploadToVault: PropTypes.func,
  currentAgent: PropTypes.object,
  availableAgents: PropTypes.array
};

export default ChatInput;